import type { Column } from '@features/columns/models'
import type { ViewColumnConfig } from '@features/views/models'

export type ColumnPinSection = 'pinned' | 'unpinned'
export type ColumnDropPosition = 'before' | 'after'

export interface ColumnPinSections {
  pinnedColumns: Column[]
  unpinnedColumns: Column[]
  labelColumnId: string | null
}

export interface ColumnPinUpdate extends ColumnPinSections {
  columnOrder: string[]
  columnVisibility: Record<string, boolean>
  columnConfigs: ViewColumnConfig[]
}

export function getExplicitPinnedColumnIds(
  columnConfigs: ViewColumnConfig[] | null | undefined
): Set<string> {
  const pinnedIds = new Set<string>()

  for (const cfg of columnConfigs ?? []) {
    if (cfg.is_pinned) pinnedIds.add(cfg.column_id)
  }

  return pinnedIds
}

/**
 * A view's pins alone. A saved view's column configs list every field (see
 * buildViewColumnConfigs); the view store keeps only the pins, which are all
 * the grid reads from them.
 */
export function pinnedColumnConfigs(
  columnConfigs: ViewColumnConfig[] | null | undefined
): ViewColumnConfig[] {
  return (columnConfigs ?? [])
    .filter((config) => config.is_pinned)
    .map((config) => ({ column_id: config.column_id, is_pinned: true }))
}

export function getEffectivePinnedColumnIds(
  orderedColumns: Column[],
  columnConfigs: ViewColumnConfig[] | null | undefined,
  defaultLabelColumnId?: string | null
): Set<string> {
  const pinnedIds = getExplicitPinnedColumnIds(columnConfigs)

  if (pinnedIds.size > 0 || orderedColumns.length === 0) {
    return pinnedIds
  }

  if (defaultLabelColumnId && orderedColumns.some((column) => column.id === defaultLabelColumnId)) {
    return new Set([defaultLabelColumnId])
  }

  return new Set([orderedColumns[0].id])
}

export function getColumnPinSections(
  orderedColumns: Column[],
  columnConfigs: ViewColumnConfig[] | null | undefined,
  defaultLabelColumnId?: string | null
): ColumnPinSections {
  const pinnedIds = getEffectivePinnedColumnIds(orderedColumns, columnConfigs, defaultLabelColumnId)
  const pinnedColumns = orderedColumns.filter((column) => pinnedIds.has(column.id))
  const unpinnedColumns = orderedColumns.filter((column) => !pinnedIds.has(column.id))

  return {
    pinnedColumns,
    unpinnedColumns,
    labelColumnId: pinnedColumns[0]?.id ?? null,
  }
}

export function toPinnedColumnConfigs(pinnedColumns: Column[]): ViewColumnConfig[] {
  return pinnedColumns.map((column) => ({
    column_id: column.id,
    is_pinned: true,
  }))
}

export function buildColumnPinUpdate(
  pinnedColumns: Column[],
  unpinnedColumns: Column[],
  columnVisibility: Record<string, boolean>
): ColumnPinUpdate {
  const orderedColumns = [...pinnedColumns, ...unpinnedColumns]
  return {
    pinnedColumns,
    unpinnedColumns,
    labelColumnId: pinnedColumns[0]?.id ?? null,
    columnOrder: orderedColumns.map((column) => column.id),
    columnVisibility,
    columnConfigs: toPinnedColumnConfigs(pinnedColumns),
  }
}

export function pinColumnToEnd(
  columnId: string,
  orderedColumns: Column[],
  columnConfigs: ViewColumnConfig[] | null | undefined,
  columnVisibility: Record<string, boolean>,
  defaultLabelColumnId?: string | null
): ColumnPinUpdate | null {
  const { pinnedColumns, unpinnedColumns } = getColumnPinSections(
    orderedColumns,
    columnConfigs,
    defaultLabelColumnId
  )
  const column = unpinnedColumns.find((item) => item.id === columnId)
  if (!column) return null

  return buildColumnPinUpdate(
    [...pinnedColumns, column],
    unpinnedColumns.filter((item) => item.id !== columnId),
    { ...columnVisibility, [columnId]: true }
  )
}

export function unpinColumnToTop(
  columnId: string,
  orderedColumns: Column[],
  columnConfigs: ViewColumnConfig[] | null | undefined,
  columnVisibility: Record<string, boolean>,
  defaultLabelColumnId?: string | null
): ColumnPinUpdate | null {
  const { pinnedColumns, unpinnedColumns } = getColumnPinSections(
    orderedColumns,
    columnConfigs,
    defaultLabelColumnId
  )
  if (pinnedColumns.length <= 1) return null

  const column = pinnedColumns.find((item) => item.id === columnId)
  if (!column) return null

  return buildColumnPinUpdate(
    pinnedColumns.filter((item) => item.id !== columnId),
    [column, ...unpinnedColumns],
    { ...columnVisibility, [columnId]: true }
  )
}

export function hidePinnedColumnToTop(
  columnId: string,
  orderedColumns: Column[],
  columnConfigs: ViewColumnConfig[] | null | undefined,
  columnVisibility: Record<string, boolean>,
  defaultLabelColumnId?: string | null
): ColumnPinUpdate | null {
  const { pinnedColumns, unpinnedColumns } = getColumnPinSections(
    orderedColumns,
    columnConfigs,
    defaultLabelColumnId
  )
  if (pinnedColumns.length <= 1) return null

  const column = pinnedColumns.find((item) => item.id === columnId)
  if (!column) return null

  return buildColumnPinUpdate(
    pinnedColumns.filter((item) => item.id !== columnId),
    [column, ...unpinnedColumns],
    { ...columnVisibility, [columnId]: false }
  )
}

export function hideAllExceptFirstPinnedColumn(
  orderedColumns: Column[],
  columnConfigs: ViewColumnConfig[] | null | undefined,
  columnVisibility: Record<string, boolean>,
  defaultLabelColumnId?: string | null
): ColumnPinUpdate {
  const { pinnedColumns, unpinnedColumns } = getColumnPinSections(
    orderedColumns,
    columnConfigs,
    defaultLabelColumnId
  )
  const labelColumn = pinnedColumns[0]
  const otherPinnedColumns = pinnedColumns.slice(1)
  const nextVisibility = { ...columnVisibility }

  if (labelColumn) nextVisibility[labelColumn.id] = true

  for (const column of [...otherPinnedColumns, ...unpinnedColumns]) {
    nextVisibility[column.id] = false
  }

  return buildColumnPinUpdate(
    labelColumn ? [labelColumn] : [],
    [...otherPinnedColumns, ...unpinnedColumns],
    nextVisibility
  )
}

function withoutColumn(columns: Column[], columnId: string): Column[] {
  return columns.filter((column) => column.id !== columnId)
}

function insertColumn(columns: Column[], column: Column, targetIndex: number): Column[] {
  const next = [...columns]
  next.splice(Math.max(0, Math.min(targetIndex, next.length)), 0, column)
  return next
}

function adjustSameSectionTargetIndex(sourceIndex: number, targetIndex: number): number {
  return sourceIndex < targetIndex ? targetIndex - 1 : targetIndex
}

export function moveColumnInPinSections(
  columnId: string,
  targetSection: ColumnPinSection,
  targetIndex: number,
  orderedColumns: Column[],
  columnConfigs: ViewColumnConfig[] | null | undefined,
  columnVisibility: Record<string, boolean>,
  defaultLabelColumnId?: string | null
): ColumnPinUpdate | null {
  const { pinnedColumns, unpinnedColumns } = getColumnPinSections(
    orderedColumns,
    columnConfigs,
    defaultLabelColumnId
  )
  const sourceSection = pinnedColumns.some((column) => column.id === columnId)
    ? 'pinned'
    : 'unpinned'
  const column = orderedColumns.find((item) => item.id === columnId)
  if (!column) return null
  if (sourceSection === 'pinned' && targetSection === 'unpinned' && pinnedColumns.length <= 1) {
    return null
  }

  let nextPinned = withoutColumn(pinnedColumns, columnId)
  let nextUnpinned = withoutColumn(unpinnedColumns, columnId)
  const nextVisibility = { ...columnVisibility }

  if (targetSection === 'pinned') {
    nextPinned = insertColumn(nextPinned, column, targetIndex)
    nextVisibility[columnId] = true
  } else {
    nextUnpinned = insertColumn(nextUnpinned, column, targetIndex)
  }

  return buildColumnPinUpdate(nextPinned, nextUnpinned, nextVisibility)
}

export function moveColumnByDropPosition(
  columnId: string,
  overColumnId: string,
  dropPosition: ColumnDropPosition,
  orderedColumns: Column[],
  columnConfigs: ViewColumnConfig[] | null | undefined,
  columnVisibility: Record<string, boolean>,
  defaultLabelColumnId?: string | null
): ColumnPinUpdate | null {
  if (columnId === overColumnId) return null

  const { pinnedColumns, unpinnedColumns } = getColumnPinSections(
    orderedColumns,
    columnConfigs,
    defaultLabelColumnId
  )
  const column = orderedColumns.find((item) => item.id === columnId)
  if (!column) return null

  const sourcePinnedIndex = pinnedColumns.findIndex((item) => item.id === columnId)
  const sourceUnpinnedIndex = unpinnedColumns.findIndex((item) => item.id === columnId)
  const overPinnedIndex = pinnedColumns.findIndex((item) => item.id === overColumnId)
  const overUnpinnedIndex = unpinnedColumns.findIndex((item) => item.id === overColumnId)
  const sourceSection: ColumnPinSection = sourcePinnedIndex !== -1 ? 'pinned' : 'unpinned'
  const targetSection: ColumnPinSection = overPinnedIndex !== -1 ? 'pinned' : 'unpinned'
  const overIndex = targetSection === 'pinned' ? overPinnedIndex : overUnpinnedIndex

  if (
    (sourceSection === 'pinned' && sourcePinnedIndex === -1) ||
    (sourceSection === 'unpinned' && sourceUnpinnedIndex === -1) ||
    overIndex === -1
  ) {
    return null
  }

  const sourceIndex = sourceSection === 'pinned' ? sourcePinnedIndex : sourceUnpinnedIndex
  let targetIndex = overIndex + (dropPosition === 'after' ? 1 : 0)
  const isPinnedBoundaryDrop =
    targetSection === 'pinned' && overIndex === pinnedColumns.length - 1 && dropPosition === 'after'
  const isUnpinnedBoundaryDrop =
    targetSection === 'unpinned' && overIndex === 0 && dropPosition === 'before'

  if (sourceSection === 'unpinned' && isPinnedBoundaryDrop) {
    return null
  }

  if (sourceSection === 'pinned' && pinnedColumns.length > 1 && isUnpinnedBoundaryDrop) {
    return null
  }

  if (sourceSection === targetSection) {
    targetIndex = adjustSameSectionTargetIndex(sourceIndex, targetIndex)
  }

  const nextVisibility = { ...columnVisibility }
  let nextPinned = withoutColumn(pinnedColumns, columnId)
  let nextUnpinned = withoutColumn(unpinnedColumns, columnId)

  if (targetSection === 'pinned') {
    nextPinned = insertColumn(nextPinned, column, targetIndex)
    nextVisibility[columnId] = true
  } else if (sourceSection === 'pinned' && pinnedColumns.length === 1) {
    const replacement =
      unpinnedColumns.find((item) => columnVisibility[item.id] !== false) ?? unpinnedColumns[0]
    if (!replacement) return null

    const replacementIndex = unpinnedColumns.findIndex((item) => item.id === replacement.id)
    if (replacementIndex < targetIndex) {
      targetIndex -= 1
    }

    nextPinned = [replacement]
    nextVisibility[replacement.id] = true
    nextUnpinned = unpinnedColumns.filter((item) => item.id !== replacement.id)
    nextUnpinned = insertColumn(nextUnpinned, column, targetIndex)
  } else {
    nextUnpinned = insertColumn(nextUnpinned, column, targetIndex)
  }

  return buildColumnPinUpdate(nextPinned, nextUnpinned, nextVisibility)
}
